import React, { useEffect, useMemo, useRef, useState } from "react";
import { Global } from "../helpers/Global";
import Select from "react-select";

export const CrearIntervencionManualGerente = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [centros, setCentros] = useState([]);

  const [usuarioId, setUsuarioId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [ordenId, setOrdenId] = useState("");
  const [centroId, setCentroId] = useState("");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [comentario, setComentario] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [cargandoCentros, setCargandoCentros] = useState(false);

  // Para distinguir cambio manual de cliente vs. asignado por la OT
  const clienteCambiadoManualmente = useRef(false);

  // Cargar catálogos
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [resSub, resCli, resOts] = await Promise.all([
          fetch(`${Global.url}usuarios/subordinados`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}clientes`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}ordenes`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);

        const [dataSub, dataCli, dataOts] = await Promise.all([
          resSub.ok ? resSub.json() : [],
          resCli.ok ? resCli.json() : [],
          resOts.ok ? resOts.json() : [],
        ]);

        // Filtra OTs activas como haces en gerente
        const otsActivas = (Array.isArray(dataOts) ? dataOts : []).filter(
          (o) => !["cancelada", "finalizada"].includes((o.estado || "").toLowerCase())
        );

        setUsuarios(Array.isArray(dataSub) ? dataSub : []);
        setClientes(Array.isArray(dataCli) ? dataCli : []);
        setOrdenes(otsActivas);
      } catch (err) {
        console.error("Error al cargar datos iniciales:", err);
        setMensaje("❌ Error al cargar datos.");
        setTimeout(() => setMensaje(""), 4000);
      }
    };

    fetchAll();
  }, []);

  // Cuando cambia la OT: fija cliente y carga centros
  useEffect(() => {
    const fetchCentros = async () => {
      if (!ordenId) {
        setCentros([]);
        setCentroId("");
        return;
      }

      const ot = ordenes.find((o) => o.id === parseInt(ordenId));
      const cliId = ot?.cliente_id ? String(ot.cliente_id) : "";
      setClienteId(cliId);

      setCargandoCentros(true);
      try {
        const res = await fetch(`${Global.url}ordenes/${ordenId}/centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        const centrosData = Array.isArray(data) ? data : data.data || [];
        setCentros(centrosData);
      } catch (err) {
        console.error("Error al obtener centros:", err);
        setCentros([]);
      } finally {
        setCargandoCentros(false);
      }
    };

    fetchCentros();
  }, [ordenId, ordenes]);

  // Si el cliente cambia manualmente, limpia OT/centros/centro
  useEffect(() => {
    if (!clienteId) return;
    if (clienteCambiadoManualmente.current) {
      setOrdenId("");
      setCentros([]);
      setCentroId("");
    }
    clienteCambiadoManualmente.current = false;
  }, [clienteId]);

  // Opciones react-select
  const usuarioOptions = useMemo(
    () => [
      { value: "", label: "Selecciona un usuario" },
      ...usuarios
        .slice()
        .sort((a, b) =>
          (a.nombre + (a.apellidos || "")).localeCompare(b.nombre + (b.apellidos || ""))
        )
        .map((u) => ({ value: u.id.toString(), label: `${u.nombre} ${u.apellidos || ""}`.trim() })),
    ],
    [usuarios]
  );

  // OTs filtradas por cliente si éste está seleccionado
  const ordenesFiltradas = useMemo(
    () =>
      clienteId
        ? ordenes.filter((o) => String(o.cliente_id) === String(clienteId))
        : ordenes,
    [ordenes, clienteId]
  );

  const ordenOptions = useMemo(
    () => [
      { value: "", label: "Selecciona una OT" },
      ...ordenesFiltradas
        .slice()
        .sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""))
        .map((o) => ({ value: o.id.toString(), label: o.titulo || `OT #${o.id}` })),
    ],
    [ordenesFiltradas]
  );

  const clienteOptions = useMemo(
    () => [
      { value: "", label: "Selecciona un cliente" },
      ...clientes
        .slice()
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
        .map((c) => ({ value: c.id.toString(), label: c.nombre })),
    ],
    [clientes]
  );

  const centroOptions = useMemo(
    () => [
      { value: "", label: "Selecciona un centro" },
      ...centros
        .slice()
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
        .map((c) => ({ value: c.id.toString(), label: c.nombre })),
    ],
    [centros]
  );

  // Helper para value controlado en Select
  const valueFrom = (options, value) =>
    value
      ? options.find((opt) => String(opt.value) === String(value)) || null
      : options[0] || null;

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!usuarioId || !clienteId || !ordenId || !centroId || !inicio || !fin) {
      setMensaje("Todos los campos son obligatorios.");
      setTimeout(() => setMensaje(""), 3000);
      return;
    }

    const payload = {
      usuario_id: parseInt(usuarioId),
      cliente_id: parseInt(clienteId),
      orden_id: parseInt(ordenId),
      centro_id: parseInt(centroId),
      inicio,
      fin,
      comentarios: comentario,
    };

    try {
      const res = await fetch(`${Global.url}ticajes/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setMensaje("✅ Intervención creada correctamente.");
        // reset
        setUsuarioId("");
        setClienteId("");
        setOrdenId("");
        setCentroId("");
        setCentros([]);
        setInicio("");
        setFin("");
        setComentario("");
      } else {
        const data = await res.json().catch(() => ({}));
        setMensaje(`❌ Error: ${data.message || "No se pudo crear la intervención."}`);
      }
    } catch (error) {
      console.error("Error al crear intervención:", error);
      setMensaje("❌ Error de conexión con el servidor.");
    } finally {
      setTimeout(() => setMensaje(""), 4000);
    }
  };

  return (
    <div className="form-jornada-wrapper">
      <h2>Crear Intervención Manual (Gerente)</h2>
      {mensaje && <p className="confirmacion">{mensaje}</p>}

      <form className="form-jornada-manual" onSubmit={handleSubmit}>
        {/* Usuario */}
        <label>Usuario:</label>
        <Select
          options={usuarioOptions}
          value={valueFrom(usuarioOptions, usuarioId)}
          onChange={(opt) => setUsuarioId(opt?.value || "")}
          isClearable
          className="select-usuario rs-full"
          classNamePrefix="select"
          placeholder="Selecciona un usuario"
        />

        {/* Cliente (seleccionable → filtra OTs) */}
        <label>Cliente:</label>
        <Select
          options={clienteOptions}
          value={valueFrom(clienteOptions, clienteId)}
          onChange={(opt) => {
            clienteCambiadoManualmente.current = true;
            setClienteId(opt?.value || "");
          }}
          isClearable
          className="select-cliente rs-full"
          classNamePrefix="select"
          placeholder="Selecciona un cliente"
        />

        {/* OT (si hay cliente, ya filtradas) */}
        <label>Orden de Trabajo:</label>
        <Select
          options={ordenOptions}
          value={valueFrom(ordenOptions, ordenId)}
          onChange={(opt) => {
            setOrdenId(opt?.value || "");
            setCentroId(""); // reset centro al cambiar OT
          }}
          isClearable
          className="select-orden rs-full"
          classNamePrefix="select"
          placeholder={clienteId ? "Selecciona una OT del cliente" : "Selecciona una OT"}
          noOptionsMessage={() => (clienteId ? "Sin OTs para este cliente" : "No hay OTs")}
        />

        {/* Centro (derivado de la OT) */}
        <label>Centro:</label>
        <Select
          options={centroOptions}
          value={valueFrom(centroOptions, centroId)}
          onChange={(opt) => setCentroId(opt?.value || "")}
          isClearable
          isDisabled={!ordenId || cargandoCentros || centroOptions.length <= 1}
          className="select-centro rs-full"
          classNamePrefix="select"
          placeholder={cargandoCentros ? "Cargando centros..." : "Selecciona un centro"}
          noOptionsMessage={() => (ordenId ? "Sin centros para esta OT" : "Selecciona antes una OT")}
        />

        {/* Fechas */}
        <label>Inicio:</label>
        <input
          className="input-layout"
          type="datetime-local"
          value={inicio}
          onChange={(e) => setInicio(e.target.value)}
          required
        />

        <label>Fin:</label>
        <input
          className="input-layout"
          type="datetime-local"
          value={fin}
          onChange={(e) => setFin(e.target.value)}
          required
        />

        {/* Comentario */}
        <label>Motivo o comentario:</label>
        <textarea
          className="input-layout"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          placeholder="Motivo o notas sobre la intervención"
          rows={3}
        />

        <button type="submit" className="btn-guardar">Crear intervención</button>
      </form>
    </div>
  );
};
